import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, renderList as _renderList, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "wh-modal" }
const _hoisted_2 = { class: "wh-modal__body" }
const _hoisted_3 = {
  key: 0,
  class: "wh-modal__state"
}
const _hoisted_4 = {
  key: 1,
  class: "wh-modal__state wh-modal__state--error"
}
const _hoisted_5 = { class: "wh-section" }
const _hoisted_6 = {
  key: 0,
  class: "wh-picker__value"
}
const _hoisted_7 = {
  key: 1,
  class: "wh-picker__placeholder"
}
const _hoisted_8 = { class: "wh-section" }
const _hoisted_9 = { class: "wh-section__label" }
const _hoisted_10 = { class: "wh-section__hint" }
const _hoisted_11 = { class: "wh-table" }
const _hoisted_12 = { class: "wh-table__icon" }
const _hoisted_13 = { class: "wh-table__cls" }
const _hoisted_14 = { class: "wh-table__val" }
const _hoisted_15 = ["data-testid", "onClick"]
const _hoisted_16 = {
  key: 0,
  class: "wh-picker__value"
}
const _hoisted_17 = {
  key: 1,
  class: "wh-picker__placeholder"
}
const _hoisted_18 = ["onClick"]
const _hoisted_19 = { class: "wh-modal__footer" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = ["disabled"]
const _hoisted_22 = {
  key: 0,
  class: "wh-footer__saved",
  "data-testid": "ifc.whmap.savedBadge"
}
const _hoisted_23 = ["title"]

import { ref, onMounted, computed } from 'vue';
import { SearchScreen } from 'Core/Screens/SearchScreen/SearchScreen';
import { IfcApi } from 'Core/Components/Ifc/IfcApi';
import IfcIcon from 'Core/Components/Ifc/IfcIcon.vue';

interface WarehousePick { id: number; name: string; }


export default /*@__PURE__*/_defineComponent({
  __name: 'WarehouseRoutingModal',
  props: {
    ifcClasses: {},
    initialDefault: {},
    initialMappings: {}
  },
  emits: ["apply", "close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const entityId = ref<number>(0);
const loading = ref(false);
const error = ref('');

const defaultWarehouse = ref<WarehousePick | null>(props.initialDefault);
const mappings = ref<Record<string, WarehousePick | null>>({});

// Persisted automapping (WAREHOUSES / IFCCLASSES sub-table) state
const autoCount = ref(0);
const savingAuto = ref(false);
const savedAuto = ref(false);
const autoError = ref('');

const toPromise = <T,>(call: { then: (cb: (value: T) => void) => unknown; fail: (cb: (err: unknown) => void) => unknown }) =>
	new Promise<T>((resolve, reject) => { call.then(resolve).fail(reject); });

onMounted(async () => {
	for (const cls of props.ifcClasses) {
		mappings.value[cls] = props.initialMappings[cls] ?? null;
	}

	loading.value = true;
	try {
		const resp = await toPromise<{ entityId: number }>(IfcApi.GetWarehousesEntityId());
		entityId.value = resp.entityId ?? 0;
		if (!entityId.value) {
			error.value = 'WAREHOUSES table not found.';
		}
	} catch (err: any) {
		error.value = err?.responseJSON?.Message || err?.message || 'Failed to resolve WAREHOUSES table.';
	} finally {
		loading.value = false;
	}

	// Preselect from the persisted automapping. On OPEN the automapping is the
	// source of truth: it overrides whatever initialMappings carried in, because
	// the parent's state may hold a stale automapping seed from parse time
	// (saving a new mapping and reopening must show the saved value). Only picks
	// made inside the current modal session override it — those happen after
	// this runs. Classes the automapping doesn't cover keep their initial pick.
	await loadAutomapping();
});

// Applies the persisted automapping to the listed class rows (overwriting
// current picks; uncovered classes keep theirs). Runs on open and on the
// Automap button. Class match is case-insensitive so serializer casing never
// breaks the lookup.
const loadAutomapping = async () => {
	if (!entityId.value) return;
	try {
		const [warehouses, mapResp] = await Promise.all([
			toPromise<Array<{ id: number; name: string }>>(IfcApi.GetWarehouses()),
			toPromise<{ mappings: Record<string, number> }>(IfcApi.GetWarehouseClassMappings()),
		]);
		const nameById = new Map<number, string>(warehouses.map(w => [w.id, w.name]));
		const auto = new Map<string, number>(
			Object.entries(mapResp?.mappings ?? {}).map(([cls, id]) => [cls.toLowerCase(), Number(id)]),
		);
		let preselected = 0;
		for (const cls of props.ifcClasses) {
			const warehouseId = auto.get(cls.toLowerCase());
			if (warehouseId && warehouseId > 0) {
				mappings.value[cls] = { id: warehouseId, name: nameById.get(warehouseId) ?? `#${warehouseId}` };
				preselected++;
			}
		}
		autoCount.value = preselected;
	} catch { /* automapping unavailable — manual mapping only */ }
};

const autoLoading = ref(false);
const applyAutomapping = async () => {
	if (autoLoading.value) return;
	autoLoading.value = true;
	try {
		await loadAutomapping();
	} finally {
		autoLoading.value = false;
	}
};

const saveAutomapping = async () => {
	if (!entityId.value || savingAuto.value) return;
	savingAuto.value = true;
	savedAuto.value = false;
	autoError.value = '';
	const out: Record<string, number> = {};
	for (const [cls, wh] of Object.entries(mappings.value)) {
		if (wh) out[cls] = wh.id;
	}
	try {
		await toPromise(IfcApi.SaveWarehouseClassMappings([...props.ifcClasses], out));
		savedAuto.value = true;
		setTimeout(() => { savedAuto.value = false; }, 2500);
	} catch (err: any) {
		autoError.value = err?.responseJSON?.Message || err?.message || 'Failed to save the automapping.';
	} finally {
		savingAuto.value = false;
	}
};

const pickWarehouse = (onSelected: (pick: WarehousePick) => void) => {
	if (!entityId.value) return;
	const searchScreen = new SearchScreen({ EntityId: entityId.value, SearchTerm: '', ButtonAdd: false });
	searchScreen.On('RECORD_SELECTED', null, (eventArgs: any) => {
		const data = eventArgs?.data;
		if (!data?.RecordId) return;
		onSelected({ id: data.RecordId, name: data.Name ?? `#${data.RecordId}` });
	});
	searchScreen.Show();
};

const pickDefault = () => pickWarehouse(pick => { defaultWarehouse.value = pick; });
const clearDefault = (e: Event) => { e.stopPropagation(); defaultWarehouse.value = null; };

const pickForClass = (cls: string) => pickWarehouse(pick => { mappings.value[cls] = pick; });
const clearForClass = (cls: string, e: Event) => { e.stopPropagation(); mappings.value[cls] = null; };

const mappedCount = computed(() => Object.values(mappings.value).filter(v => !!v).length);

const onApply = () => {
	const out: Record<string, WarehousePick> = {};
	for (const [cls, wh] of Object.entries(mappings.value)) {
		if (wh) out[cls] = wh;
	}
	emit('apply', {
		defaultWarehouse: defaultWarehouse.value,
		mappings: out,
	});
};

const onClose = () => emit('close');

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[13] || (_cache[13] = _createElementVNode("div", { class: "wh-modal__header" }, [
      _createElementVNode("i", { class: "fa fa-truck" }),
      _createElementVNode("span", null, "Warehouse routing")
    ], -1 /* CACHED */)),
    _createElementVNode("div", _hoisted_2, [
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[0] || (_cache[0] = [
            _createElementVNode("i", { class: "fa fa-spinner fa-spin" }, null, -1 /* CACHED */),
            _createTextVNode(" Loading… ", -1 /* CACHED */)
          ]))]))
        : (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _cache[1] || (_cache[1] = _createElementVNode("i", { class: "fa fa-exclamation-triangle" }, null, -1 /* CACHED */)),
              _createTextVNode(" " + _toDisplayString(error.value), 1 /* TEXT */)
            ]))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createCommentVNode(" Default (fallback) "),
              _createElementVNode("section", _hoisted_5, [
                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "wh-section__label" }, [
                  _createTextVNode(" Default warehouse "),
                  _createElementVNode("span", { class: "wh-section__hint" }, "used for classes without an explicit mapping")
                ], -1 /* CACHED */)),
                _createElementVNode("button", {
                  type: "button",
                  class: "wh-picker",
                  onClick: pickDefault
                }, [
                  _cache[3] || (_cache[3] = _createElementVNode("i", { class: "fa fa-search wh-picker__icon" }, null, -1 /* CACHED */)),
                  (defaultWarehouse.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(defaultWarehouse.value.name), 1 /* TEXT */))
                    : (_openBlock(), _createElementBlock("span", _hoisted_7, "— none (skip linking) —")),
                  (defaultWarehouse.value)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 2,
                        class: "wh-picker__clear",
                        onClick: clearDefault,
                        title: "Clear"
                      }, [...(_cache[2] || (_cache[2] = [
                        _createElementVNode("i", { class: "fa fa-times" }, null, -1 /* CACHED */)
                      ]))]))
                    : _createCommentVNode("v-if", true)
                ])
              ]),
              _createCommentVNode(" Per-class overrides "),
              _createElementVNode("section", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  _cache[5] || (_cache[5] = _createTextVNode(" Per-class overrides ", -1 /* CACHED */)),
                  _createElementVNode("span", _hoisted_10, [
                    _createTextVNode(_toDisplayString(mappedCount.value) + " / " + _toDisplayString(__props.ifcClasses.length) + " mapped", 1 /* TEXT */),
                    (autoCount.value > 0)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(" · " + _toDisplayString(autoCount.value) + " from automapping", 1 /* TEXT */)
                        ], 64 /* STABLE_FRAGMENT */))
                      : _createCommentVNode("v-if", true)
                  ])
                ]),
                _createElementVNode("table", _hoisted_11, [
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.ifcClasses, (cls) => {
                      return (_openBlock(), _createElementBlock("tr", { key: cls }, [
                        _createElementVNode("td", _hoisted_12, [
                          _createVNode(IfcIcon, {
                            "ifc-class": cls,
                            size: 16
                          }, null, 8 /* PROPS */, ["ifc-class"])
                        ]),
                        _createElementVNode("td", _hoisted_13, _toDisplayString(cls), 1 /* TEXT */),
                        _createElementVNode("td", _hoisted_14, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "wh-picker",
                            "data-testid": 'ifc.whmap.' + cls,
                            onClick: ($event: any) => (pickForClass(cls))
                          }, [
                            _cache[7] || (_cache[7] = _createElementVNode("i", { class: "fa fa-search wh-picker__icon" }, null, -1 /* CACHED */)),
                            (mappings.value[cls])
                              ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(mappings.value[cls]!.name), 1 /* TEXT */))
                              : (_openBlock(), _createElementBlock("span", _hoisted_17, "— use default —")),
                            (mappings.value[cls])
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: 2,
                                  class: "wh-picker__clear",
                                  onClick: (e) => clearForClass(cls, e),
                                  title: "Clear"
                                }, [...(_cache[6] || (_cache[6] = [
                                  _createElementVNode("i", { class: "fa fa-times" }, null, -1 /* CACHED */)
                                ]))], 8 /* PROPS */, _hoisted_18))
                              : _createCommentVNode("v-if", true)
                          ], 8 /* PROPS */, _hoisted_15)
                        ])
                      ]))
                    }), 128 /* KEYED_FRAGMENT */))
                  ])
                ])
              ])
            ], 64 /* STABLE_FRAGMENT */))
    ]),
    _createElementVNode("div", _hoisted_19, [
      _createElementVNode("button", {
        class: "btn btn-default",
        "data-testid": "ifc.whmap.automap",
        disabled: !entityId.value || autoLoading.value,
        title: "Apply the persisted automapping to the listed classes",
        onClick: applyAutomapping
      }, [
        _createElementVNode("i", {
          class: _normalizeClass(["fa-solid", autoLoading.value ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'])
        }, null, 2 /* CLASS */),
        _cache[8] || (_cache[8] = _createTextVNode(" Automap ", -1 /* CACHED */))
      ], 8 /* PROPS */, _hoisted_20),
      _createElementVNode("button", {
        class: "btn btn-default",
        "data-testid": "ifc.whmap.saveAutomapping",
        disabled: !entityId.value || savingAuto.value,
        title: "Persist the current per-class picks into the WAREHOUSES / IFCCLASSES automapping",
        onClick: saveAutomapping
      }, [
        _createElementVNode("i", {
          class: _normalizeClass(["fa-solid", savingAuto.value ? 'fa-spinner fa-spin' : 'fa-save'])
        }, null, 2 /* CLASS */),
        _cache[9] || (_cache[9] = _createTextVNode(" Save automapping ", -1 /* CACHED */))
      ], 8 /* PROPS */, _hoisted_21),
      (savedAuto.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_22, [...(_cache[10] || (_cache[10] = [
            _createElementVNode("i", { class: "fa fa-check" }, null, -1 /* CACHED */),
            _createTextVNode(" Saved", -1 /* CACHED */)
          ]))]))
        : (autoError.value)
          ? (_openBlock(), _createElementBlock("span", {
              key: 1,
              class: "wh-footer__error",
              title: autoError.value
            }, _toDisplayString(autoError.value), 9 /* TEXT, PROPS */, _hoisted_23))
          : _createCommentVNode("v-if", true),
      _cache[12] || (_cache[12] = _createElementVNode("span", { class: "wh-footer__spacer" }, null, -1 /* CACHED */)),
      _createElementVNode("button", {
        class: "btn btn-default",
        onClick: onClose
      }, "Cancel"),
      _createElementVNode("button", {
        class: "btn btn-primary",
        "data-testid": "ifc.whmap.apply",
        onClick: onApply
      }, [...(_cache[11] || (_cache[11] = [
        _createElementVNode("i", { class: "fa fa-check" }, null, -1 /* CACHED */),
        _createTextVNode(" Apply", -1 /* CACHED */)
      ]))])
    ])
  ]))
}
}

})